import { Box, Stack, Text, useMantineTheme } from '@mantine/core'
import { IconCheck } from '@tabler/icons-react'
import React, { Dispatch, SetStateAction, useEffect, useMemo, useState } from 'react'

interface ColorPickerProps {
  min: number
  max: number
  colors: string[]
  keyName: string
  draftSelectedColors: string[]
  setProperties: Dispatch<SetStateAction<{ [key: string]: any }>>
  setDisableContinue: Dispatch<SetStateAction<boolean>>
}

const ColorPicker: React.FC<ColorPickerProps> = ({
  min = 1,
  max = 1,
  colors,
  keyName,
  draftSelectedColors,
  setProperties,
  setDisableContinue,
}) => {
  const theme = useMantineTheme()
  const [selectedColors, setSelectedColors] = useState<string[]>(
    draftSelectedColors || colors.slice(0, min)
  )

  const handleColorClick = (color: string) => {
    if (min === 1 && max === 1) {
      setSelectedColors([color])
      return
    }
    if (selectedColors.includes(color)) {
      setSelectedColors(selectedColors.filter(c => c !== color))
    } else if (selectedColors.length < max) setSelectedColors([...selectedColors, color])
  }

  useEffect(() => {
    setProperties(prev => ({
      ...prev,
      [keyName]: selectedColors,
    }))
  }, [selectedColors, setProperties])

  useEffect(() => {
    if (selectedColors.length > 0 && selectedColors.length < min) {
      setDisableContinue(true)
    } else setDisableContinue(false)
  }, [selectedColors.length, min])

  const colorPicker = useMemo(() => {
    const selectedColorIndices = selectedColors.map(selectedColor => colors.indexOf(selectedColor))

    return colors.map((color, index) => (
      <Box
        key={color}
        style={{
          borderRadius: '50%',
          width: '33px',
          height: '33px',
          backgroundColor: color,
          border: selectedColorIndices.includes(index)
            ? `2px solid ${theme.colors.l1Primary[0]}`
            : `1px solid ${theme.colors.splitter[0]}`,
          position: 'relative',
          cursor: 'pointer',
        }}
        onClick={() => handleColorClick(color)}
      >
        {selectedColorIndices.includes(index) && (
          <IconCheck
            size={10}
            style={{
              position: 'absolute',
              top: '-5px',
              right: '-5px',
              width: '12px',
              height: '12px',
              backgroundColor: theme.colors.l1Primary[0],
              color: theme.colors.background[0],
              borderRadius: '50%',
              border: `2px solid ${theme.colors.background[0]}`,
            }}
          />
        )}
      </Box>
    ))
  }, [selectedColors, colors])

  return (
    <>
      <Stack style={{ marginTop: '-12px', flexDirection: 'row', gap: '10px' }}>
        <Text
          style={{
            color: theme.colors.paragraph[0],
            fontSize: '12px',
          }}
        >
          {min === 1 && max === 1 ? `Select 1 color` : `Select ${min} to ${max} colors`}
        </Text>
        {selectedColors.length > 0 && selectedColors.length < min && (
          <Text style={{ color: theme.colors.notification[0], fontSize: '12px' }}>
            Select {min - selectedColors.length} more
          </Text>
        )}
      </Stack>
      <Stack style={{ flexDirection: 'row', flexWrap: 'wrap', gap: '10px' }}>{colorPicker}</Stack>
    </>
  )
}

export default ColorPicker
